/**
 * Project Management Service
 * 
 * Handles all Firebase Firestore operations for the project management module.
 * Provides CRUD operations for boards, tasks, comments, and attachments.
 * 
 * Collections Structure:
 * - projects/ - Board documents
 * - projects/{boardId}/tasks/ - Task cards subcollection
 * - projects/{boardId}/tasks/{taskId}/comments/ - Comments subcollection
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  writeBatch,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  ProjectBoard,
  TaskCard,
  Comment,
  BoardProperty,
  ViewSettings,
  BoardColumn,
} from '../types';

// ==========================================
// COLLECTION REFERENCES (Dynamic based on adminId)
// ==========================================

// Projects are now stored as: report-user-ids/{adminId}/projects/{projectId}
/* Removed unused getProjectsPath */

const TASKS_SUBCOLLECTION = 'tasks';
const COMMENTS_SUBCOLLECTION = 'comments';

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Convert Firestore Timestamp to JavaScript Date
 */
const convertTimestamp = (timestamp: any): Date => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp);
};

/**
 * Convert JavaScript Date to Firestore Timestamp
 */
const toTimestamp = (date: Date | undefined): Timestamp | undefined => {
  return date ? Timestamp.fromDate(date) : undefined;
};

/**
 * Convert Firestore document to ProjectBoard type
 */
const docToBoard = (doc: QueryDocumentSnapshot<DocumentData>): ProjectBoard => {
  const data = doc.data();
  return {
    id: doc.id,
    title: data.title,
    description: data.description || '',
    icon: data.icon,
    createdBy: data.createdBy,
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
    teamId: data.teamId,
    isTemplate: data.isTemplate || false,
    properties: data.properties || [],
    viewSettings: data.viewSettings || getDefaultViewSettings(),
    columnOrder: data.columnOrder,
  };
};

/**
 * Convert Firestore document to TaskCard type
 */
const docToTask = (doc: QueryDocumentSnapshot<DocumentData>): TaskCard => {
  const data = doc.data();
  return {
    id: doc.id,
    boardId: data.boardId,
    title: data.title,
    description: data.description,
    status: data.status,
    priority: data.priority,
    assignee: data.assignee,
    dueDate: data.dueDate ? convertTimestamp(data.dueDate) : undefined,
    tags: data.tags || [],
    properties: data.properties || {},
    createdBy: data.createdBy,
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
    order: data.order || 0,
    attachments: data.attachments || [],
    commentCount: data.commentCount || 0,
    imageUrl: data.imageUrl, // Include imageUrl field from Firebase
  };
};

/**
 * Get default view settings for a new board
 */
const getDefaultViewSettings = (): ViewSettings => ({
  viewType: 'kanban',
  groupBy: 'status',
  sortBy: 'order',
  sortDirection: 'asc',
  filterRules: [],
  visibleProperties: ['assignee', 'dueDate', 'priority', 'tags'],
  cardLayout: 'normal',
});

/**
 * Get default columns for a new board
 */
export const getDefaultColumns = (): BoardColumn[] => [
  { id: 'todo', title: 'To Do', color: '#6B7280', order: 0 },
  { id: 'in-progress', title: 'In Progress', color: '#3B82F6', order: 1 },
  { id: 'review', title: 'Review', color: '#F59E0B', order: 2 },
  { id: 'done', title: 'Done', color: '#10B981', order: 3 },
];

/**
 * Get default properties for a new board
 */
export const getDefaultProperties = (): BoardProperty[] => [
  {
    id: 'status',
    name: 'Status',
    type: 'select',
    required: true,
    options: getDefaultColumns().map(col => ({
      id: col.id,
      value: col.title,
      color: col.color,
    })),
  },
  {
    id: 'priority',
    name: 'Priority',
    type: 'select',
    options: [
      { id: 'low', value: 'Low', color: '#6B7280' },
      { id: 'medium', value: 'Medium', color: '#3B82F6' },
      { id: 'high', value: 'High', color: '#F59E0B' },
      { id: 'urgent', value: 'Urgent', color: '#EF4444' },
    ],
  },
  {
    id: 'assignee',
    name: 'Assignee',
    type: 'person',
  },
  {
    id: 'dueDate',
    name: 'Due Date',
    type: 'date',
  },
  {
    id: 'tags',
    name: 'Tags',
    type: 'multiselect',
    options: [],
  },
];

// ==========================================
// BOARD OPERATIONS
// ==========================================

/**
 * Create a new project board
 */
export const createBoard = async (
  adminId: string,
  title: string,
  description: string,
  createdBy: string,
  icon?: string
): Promise<ProjectBoard> => {
  try {
    const now = Timestamp.now();
    const boardData: any = {
      title,
      description,
      icon: icon || '📋',
      createdBy,
      createdAt: now,
      updatedAt: now,
      isTemplate: false,
      properties: getDefaultProperties(),
      viewSettings: getDefaultViewSettings(),
      columnOrder: getDefaultColumns().map(col => col.id),
    };

    const projectsRef = collection(db, 'report-user-ids', adminId, 'projects');
    const docRef = await addDoc(projectsRef, boardData);

    return {
      id: docRef.id,
      ...boardData,
      createdAt: now.toDate(),
      updatedAt: now.toDate(),
    };
  } catch (error) {
    console.error('Error creating board:', error);
    throw error;
  }
};

/**
 * Get all boards for an admin
 */
export const getBoards = async (adminId: string): Promise<ProjectBoard[]> => {
  try {
    const projectsRef = collection(db, 'report-user-ids', adminId, 'projects');
    const snapshot = await getDocs(projectsRef);
    return snapshot.docs.map(docToBoard);
  } catch (error) {
    console.error('Error fetching boards:', error);
    throw error;
  }
};

/**
 * Get a specific board by ID
 */
export const getBoard = async (adminId: string, boardId: string): Promise<ProjectBoard | null> => {
  try {
    const docRef = doc(db, 'report-user-ids', adminId, 'projects', boardId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docToBoard(docSnap as QueryDocumentSnapshot<DocumentData>);
    }
    return null;
  } catch (error) {
    console.error('Error fetching board:', error);
    throw error;
  }
};

/**
 * Update a board
 */
export const updateBoard = async (
  adminId: string,
  boardId: string,
  updates: Partial<ProjectBoard>
): Promise<void> => {
  try {
    const docRef = doc(db, 'report-user-ids', adminId, 'projects', boardId);
    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    // Convert Date objects to Timestamps
    if (updates.createdAt) {
      updateData.createdAt = toTimestamp(updates.createdAt);
    }

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating board:', error);
    throw error;
  }
};

/**
 * Delete a board and all its tasks
 */
export const deleteBoard = async (adminId: string, boardId: string): Promise<void> => {
  try {
    const batch = writeBatch(db);

    // Delete all tasks in the board
    const tasksRef = collection(db, 'report-user-ids', adminId, 'projects', boardId, TASKS_SUBCOLLECTION);
    const tasksSnapshot = await getDocs(tasksRef);

    tasksSnapshot.docs.forEach(taskDoc => {
      batch.delete(taskDoc.ref);
    });

    // Delete the board itself
    const boardRef = doc(db, 'report-user-ids', adminId, 'projects', boardId);
    batch.delete(boardRef);

    await batch.commit();
  } catch (error) {
    console.error('Error deleting board:', error);
    throw error;
  }
};

// ==========================================
// TASK OPERATIONS
// ==========================================

/**
 * Create a new task card
 */
export const createTask = async (
  adminId: string,
  boardId: string,
  title: string,
  createdBy: string,
  status: string = 'todo',
  additionalData?: Partial<TaskCard>
): Promise<TaskCard> => {
  try {
    const now = Timestamp.now();

    // Get all tasks to find max order (simpler query, no composite index needed)
    const tasksRef = collection(db, 'report-user-ids', adminId, 'projects', boardId, TASKS_SUBCOLLECTION);
    const snapshot = await getDocs(tasksRef);

    // Find max order for this specific status
    let maxOrder = 0;
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.status === status && data.order > maxOrder) {
        maxOrder = data.order;
      }
    });

    // Create base task data
    const baseTaskData = {
      boardId,
      title,
      status,
      createdBy,
      createdAt: now,
      updatedAt: now,
      order: maxOrder + 1,
      properties: {},
      tags: [],
      commentCount: 0,
    };

    // Merge additional data, filtering out undefined values (Firebase doesn't accept undefined)
    const cleanAdditionalData = additionalData ?
      Object.fromEntries(
        Object.entries(additionalData).filter(([_, value]) => value !== undefined)
      ) : {};

    const taskData = {
      ...baseTaskData,
      ...cleanAdditionalData,
    };

    const docRef = await addDoc(tasksRef, taskData);

    return {
      id: docRef.id,
      ...taskData,
      createdAt: now.toDate(),
      updatedAt: now.toDate(),
      dueDate: additionalData?.dueDate,
      imageUrl: (taskData as any).imageUrl, // Explicitly include imageUrl in return
    };
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

/**
 * Get all tasks for a board
 */
export const getTasks = async (adminId: string, boardId: string): Promise<TaskCard[]> => {
  try {
    const tasksRef = collection(db, 'report-user-ids', adminId, 'projects', boardId, TASKS_SUBCOLLECTION);
    const snapshot = await getDocs(tasksRef);

    // Sort by order on client side
    const tasks = snapshot.docs.map(docToTask);
    return tasks.sort((a, b) => a.order - b.order);
  } catch (error) {
    console.error('Error fetching tasks:', error);
    throw error;
  }
};

/**
 * Get a specific task
 */
export const getTask = async (adminId: string, boardId: string, taskId: string): Promise<TaskCard | null> => {
  try {
    const docRef = doc(db, 'report-user-ids', adminId, 'projects', boardId, TASKS_SUBCOLLECTION, taskId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docToTask(docSnap as QueryDocumentSnapshot<DocumentData>);
    }
    return null;
  } catch (error) {
    console.error('Error fetching task:', error);
    throw error;
  }
};

/**
 * Update a task
 */
export const updateTask = async (
  adminId: string,
  boardId: string,
  taskId: string,
  updates: Partial<TaskCard>
): Promise<void> => {
  try {
    const docRef = doc(db, 'report-user-ids', adminId, 'projects', boardId, TASKS_SUBCOLLECTION, taskId);

    // Filter out undefined values (Firebase doesn't accept undefined values)
    const cleanedUpdates: any = {};
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined) {
        cleanedUpdates[key] = value;
      }
    });

    const updateData: any = {
      ...cleanedUpdates,
      updatedAt: Timestamp.now(),
    };

    // Convert Date objects to Timestamps
    if (updates.dueDate !== undefined) {
      updateData.dueDate = updates.dueDate ? toTimestamp(updates.dueDate) : null;
    }
    if (updates.createdAt) {
      updateData.createdAt = toTimestamp(updates.createdAt);
    }

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
};

/**
 * Move a task to a different status column
 * Updates the task's status and reorders tasks in both source and destination columns
 */
export const moveTask = async (
  adminId: string,
  boardId: string,
  taskId: string,
  newStatus: string,
  newOrder: number
): Promise<void> => {
  try {
    const batch = writeBatch(db);
    const taskRef = doc(db, 'report-user-ids', adminId, 'projects', boardId, TASKS_SUBCOLLECTION, taskId);

    // Update the moved task
    batch.update(taskRef, {
      status: newStatus,
      order: newOrder,
      updatedAt: Timestamp.now(),
    });

    // Reorder other tasks in the destination column
    const tasksRef = collection(db, 'report-user-ids', adminId, 'projects', boardId, TASKS_SUBCOLLECTION);
    const q = query(tasksRef, where('status', '==', newStatus), where('order', '>=', newOrder));
    const snapshot = await getDocs(q);

    snapshot.docs.forEach(doc => {
      if (doc.id !== taskId) {
        batch.update(doc.ref, { order: doc.data().order + 1 });
      }
    });

    await batch.commit();
  } catch (error) {
    console.error('Error moving task:', error);
    throw error;
  }
};

/**
 * Delete a task
 */
export const deleteTask = async (adminId: string, boardId: string, taskId: string): Promise<void> => {
  try {
    const docRef = doc(db, 'report-user-ids', adminId, 'projects', boardId, TASKS_SUBCOLLECTION, taskId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
};

// ==========================================
// COMMENT OPERATIONS
// ==========================================

/**
 * Add a comment to a task
 */
export const addComment = async (
  adminId: string,
  boardId: string,
  taskId: string,
  text: string,
  userId: string,
  userName: string,
  userEmail: string
): Promise<Comment> => {
  try {
    const now = Timestamp.now();
    const commentData = {
      cardId: taskId,
      userId,
      userName,
      userEmail,
      text,
      createdAt: now,
      mentions: [],
    };

    const commentsRef = collection(
      db,
      'report-user-ids',
      adminId,
      'projects',
      boardId,
      TASKS_SUBCOLLECTION,
      taskId,
      COMMENTS_SUBCOLLECTION
    );

    const docRef = await addDoc(commentsRef, commentData);

    // Update comment count on the task
    const taskRef = doc(db, 'report-user-ids', adminId, 'projects', boardId, TASKS_SUBCOLLECTION, taskId);
    const taskSnap = await getDoc(taskRef);
    const currentCount = taskSnap.data()?.commentCount || 0;
    await updateDoc(taskRef, { commentCount: currentCount + 1 });

    return {
      id: docRef.id,
      ...commentData,
      createdAt: now.toDate(),
    };
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

/**
 * Get all comments for a task
 */
export const getComments = async (
  adminId: string,
  boardId: string,
  taskId: string
): Promise<Comment[]> => {
  try {
    const commentsRef = collection(
      db,
      'report-user-ids',
      adminId,
      'projects',
      boardId,
      TASKS_SUBCOLLECTION,
      taskId,
      COMMENTS_SUBCOLLECTION
    );

    const snapshot = await getDocs(commentsRef);

    // Sort on client side
    const comments = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        cardId: data.cardId,
        userId: data.userId,
        userName: data.userName,
        userEmail: data.userEmail,
        text: data.text,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: data.updatedAt ? convertTimestamp(data.updatedAt) : undefined,
        mentions: data.mentions || [],
      };
    });

    return comments.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  } catch (error) {
    console.error('Error fetching comments:', error);
    throw error;
  }
};

/**
 * Delete a comment
 */
export const deleteComment = async (
  adminId: string,
  boardId: string,
  taskId: string,
  commentId: string
): Promise<void> => {
  try {
    const commentRef = doc(
      db,
      'report-user-ids',
      adminId,
      'projects',
      boardId,
      TASKS_SUBCOLLECTION,
      taskId,
      COMMENTS_SUBCOLLECTION,
      commentId
    );

    await deleteDoc(commentRef);

    // Update comment count on the task
    const taskRef = doc(db, 'report-user-ids', adminId, 'projects', boardId, TASKS_SUBCOLLECTION, taskId);
    const taskSnap = await getDoc(taskRef);
    const currentCount = taskSnap.data()?.commentCount || 0;
    await updateDoc(taskRef, { commentCount: Math.max(0, currentCount - 1) });
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
};

